// Главный экран.
//
// Что он должен сделать за три секунды: ответить «сколько я ещё могу
// потратить и не выйти из плана», дать записать трату и показать, что горит.
//
// Наверху одна приподнятая карточка: кольцо сравнивает потраченное с
// прошедшим временем месяца. Из их взаимного положения видно то, ради чего
// экран и открывают, — иду с опережением или отстаю. Само по себе
// «потрачено 60%» не значит ничего, пока не знаешь, сколько месяца прошло.
//
// Ниже — карточки по убыванию срочности, и каждая ведёт туда, где с этим
// можно что-то сделать. Прежняя сетка 2×3 из иконок убрана: она занимала
// лучшее место экрана и была меню, а не сведениями.

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/client';
import type { BudgetCategoryRow, RecentTransaction } from '../api/types';
import { TransactionModal } from '../components/TransactionModal';
import { BudgetRing } from '../components/charts/BudgetRing';
import {
  ArrowDownLeftIcon,
  ArrowUpRightIcon,
  BarsIcon,
  ChevronRightIcon,
  PulseIcon,
} from '../components/icons';
import { useViewCurrency } from '../lib/view-currency';

type DashboardProps = {
  onNavigate: (
    tab: 'debts' | 'transactions' | 'categories' | 'analytics' | 'budget' | 'health',
  ) => void;
};

const MONTHS = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];

function plural(n: number, one: string, few: string, many: string): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return many;
  if (last === 1) return one;
  if (last >= 2 && last <= 4) return few;
  return many;
}

export function Dashboard({ onNavigate }: DashboardProps): JSX.Element {
  const [add, setAdd] = useState<'expense' | 'income' | null>(null);
  const { formatShortAmount, convertMajor } = useViewCurrency();

  const kpiQ = useQuery({ queryKey: ['kpi'], queryFn: () => api.kpi() });
  const budgetQ = useQuery({ queryKey: ['budget', 'current'], queryFn: () => api.budget() });
  const recentQ = useQuery({
    queryKey: ['analytics', 'recent', 4],
    queryFn: () => api.recentTransactions(4),
  });

  const money = (minor: bigint | string): string => {
    const { value } = convertMajor(typeof minor === 'bigint' ? minor.toString() : minor);
    return Math.round(Math.abs(value))
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  if (kpiQ.isLoading) return <HomeSkeleton />;
  if (kpiQ.error) return <div className="error">Не удалось загрузить. Потяните вниз.</div>;
  if (!kpiQ.data) return <div className="error">Нет данных</div>;

  const k = kpiQ.data;
  const b = budgetQ.data;
  const now = new Date();
  const monthName = MONTHS[now.getMonth()] ?? '';
  const dayNo = b?.days_passed ?? now.getDate();
  const daysIn = b?.days_in_month ?? 30;
  const daysLeft = Math.max(0, daysIn - dayNo);

  const planned = b ? BigInt(b.allocation.expense_planned_minor) : 0n;
  const actual = b ? BigInt(b.allocation.expense_actual_minor) : 0n;
  const left = planned - actual;
  const isOver = left < 0n;
  const spentRatio = planned > 0n ? Number(actual) / Number(planned) : 0;
  const timeRatio = daysIn > 0 ? dayNo / daysIn : 0;
  const perDay = daysLeft > 0 && left > 0n ? left / BigInt(daysLeft) : 0n;

  // «Опережаю или отстаю» в деньгах: сколько по темпу времени уже можно было
  // потратить, против того, что потрачено. Понятнее процентов.
  const paceAllowance =
    planned > 0n ? (planned * BigInt(Math.round(timeRatio * 1000))) / 1000n : 0n;
  const paceDiff = paceAllowance - actual;

  const cats: BudgetCategoryRow[] = b?.categories ?? [];
  const lump = cats.filter((c) => c.kind === 'expense' && c.spending_mode === 'lump');
  const lumpOpen = lump.filter((c) => c.status !== 'paid');
  const strained = cats.filter(
    (c) => c.kind === 'expense' && c.spending_mode === 'spread' && c.status !== 'ok',
  );
  const overdue = BigInt(k.overdue_total_minor);

  return (
    <div className="screen home2">
      <header className="h2-head">
        <div className="h2-month">{monthName}</div>
        <div className="h2-day">
          {dayNo}
          <span> / {daysIn}</span>
        </div>
      </header>

      {/* Единственная приподнятая поверхность экрана — она же главный ответ. */}
      <button type="button" className="h2-hero" onClick={() => onNavigate('budget')}>
        <BudgetRing spentRatio={spentRatio} timeRatio={timeRatio}>
          <span className="ring-pct">{Math.round(spentRatio * 100)}%</span>
          <span className="ring-sub">плана</span>
        </BudgetRing>
        {/* Главное число — остаток планового конверта.
            ⚠️ Раньше здесь стояло running_balance_minor («все доходы минус все
            расходы за историю записей»), и это оказалось ловушкой: величина
            получается как разность двух почти равных больших чисел. На живых
            данных 16 100 000 дохода и 16 032 895 расхода давали 67 105 —
            крупнейшее число экрана выглядело так, будто у человека 67 тысяч
            на всё. Одна незаписанная зарплата двигает его на миллионы, а
            записать расход легче, чем доход, поэтому дрейф всегда вниз.
            Остаток плана отвечает на вопрос, ради которого экран открывают:
            сколько ещё можно потратить. */}
        <div className="h2-hero-main">
          <div className="h2-hero-label">{isOver ? 'Сверх плана' : 'Осталось'}</div>
          <div className={`h2-hero-value ${isOver ? 'neg' : ''}`}>
            {money(isOver ? -left : left)}
          </div>
          {/* Одна короткая строка: длинная уезжает на три и теряет смысл.
              Темп проговаривает фраза под карточкой. */}
          <div className="h2-hero-note">
            {!isOver && perDay > 0n ? (
              <>
                можно <span className="nowrap">{money(perDay)}</span> в день
              </>
            ) : (
              `${daysLeft} ${plural(daysLeft, 'день', 'дня', 'дней')} до конца месяца`
            )}
          </div>
        </div>
        <span className="h2-hero-go">
          <ChevronRightIcon size={18} />
        </span>
      </button>

      {/* Одна фраза, ради которой кольцо и рисуется. */}
      {b && planned > 0n ? (
        <div className={`h2-pace ${paceDiff < 0n ? 'behind' : 'ahead'}`}>
          {paceDiff < 0n
            ? `Тратишь быстрее плана на ${money(-paceDiff)}`
            : perDay > 0n
              ? `В запасе ${money(paceDiff)} — можно ${money(perDay)} в день`
              : `В запасе ${money(paceDiff)} к сегодняшнему дню`}
        </div>
      ) : null}

      <div className="h2-actions">
        <button type="button" className="h2-act expense" onClick={() => setAdd('expense')}>
          <ArrowUpRightIcon size={17} />
          Расход
        </button>
        <button type="button" className="h2-act income" onClick={() => setAdd('income')}>
          <ArrowDownLeftIcon size={17} />
          Доход
        </button>
      </div>

      {lump.length > 0 ? (
        <button type="button" className="h2-card" onClick={() => onNavigate('budget')}>
          <div className="h2-card-top">
            <span className="h2-card-title">К оплате</span>
            <span className="h2-card-meta">
              {lump.length - lumpOpen.length} из {lump.length}
              <ChevronRightIcon size={15} />
            </span>
          </div>
          {/* Обязательства месяца — отрезками: закрытые заливаются акцентом.
              Видно не только «сколько», но и «насколько близко к концу». */}
          <div className="h2-steps">
            {lump.map((c) => (
              <span
                key={c.plan_id}
                className={`h2-step ${
                  c.status === 'paid' ? 'done' : c.status === 'over' ? 'over' : ''
                }`}
              />
            ))}
          </div>
          <div className="h2-card-note">
            {lumpOpen.length === 0
              ? 'Все обязательства месяца закрыты'
              : `Осталось: ${lumpOpen
                  .slice(0, 2)
                  .map((c) => c.category_name ?? c.category_slug)
                  .join(', ')}${lumpOpen.length > 2 ? ` и ещё ${lumpOpen.length - 2}` : ''}`}
          </div>
        </button>
      ) : null}

      {strained.length > 0 ? (
        <button type="button" className="h2-card warn" onClick={() => onNavigate('budget')}>
          <div className="h2-card-top">
            <span className="h2-card-title">Выходят за план</span>
            <span className="h2-card-meta">
              {strained.length}
              <ChevronRightIcon size={15} />
            </span>
          </div>
          <div className="h2-chips">
            {strained.slice(0, 3).map((c) => (
              <span key={c.plan_id} className={`h2-chip ${c.status === 'over' ? 'over' : ''}`}>
                {c.category_name ?? c.category_slug}
                <b>{formatShortAmount(c.actual_minor)}</b>
              </span>
            ))}
          </div>
        </button>
      ) : null}

      <button type="button" className="h2-card" onClick={() => onNavigate('debts')}>
        <div className="h2-card-top">
          <span className="h2-card-title">Долг</span>
          <span className="h2-card-meta">
            весь список
            <ChevronRightIcon size={15} />
          </span>
        </div>
        <div className="h2-debt">
          <div className="h2-debt-main">{money(k.total_debt_minor)}</div>
          <div className="h2-debt-side">
            <span>{money(k.monthly_load_minor)} в месяц</span>
            {overdue > 0n ? <em>просрочено {formatShortAmount(overdue.toString())}</em> : null}
          </div>
        </div>
      </button>

      <section className="h2-recent">
        <div className="h2-card-top">
          <span className="h2-card-title">Последние</span>
          <button type="button" className="h2-link" onClick={() => onNavigate('transactions')}>
            вся история
            <ChevronRightIcon size={15} />
          </button>
        </div>
        {(recentQ.data ?? []).length === 0 ? (
          <p className="h2-empty">Записей пока нет. Первая — «обед 40к».</p>
        ) : (
          <div className="h2-tx-list">
            {(recentQ.data ?? []).map((t: RecentTransaction) => (
              <div key={t.id} className="h2-tx">
                <span className="h2-tx-name">{t.category_name ?? t.note ?? 'Без категории'}</span>
                <span className={`h2-tx-sum ${t.kind === 'income' ? 'pos' : ''}`}>
                  {t.kind === 'income' ? '+' : '−'}
                  {formatShortAmount(t.amount_minor)}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      <nav className="h2-more">
        <button type="button" onClick={() => onNavigate('analytics')}>
          <BarsIcon size={16} />
          Аналитика
        </button>
        {/* «Диагностика», а не «Здоровье»: здоровьем в этом приложении уже
            названы финансовый скоринг во вкладке Аналитики и категория трат
            (медицина). Три разных смысла на одно слово — и человек жмёт не туда. */}
        <button type="button" onClick={() => onNavigate('health')}>
          <PulseIcon size={16} />
          Диагностика
        </button>
      </nav>

      {add ? <TransactionModal mode="add" initialKind={add} onClose={() => setAdd(null)} /> : null}
    </div>
  );
}

function HomeSkeleton(): JSX.Element {
  return (
    <div className="screen home2">
      <div className="h2-hero is-skel">
        <div className="skel skel-ring" />
        <div className="skel-lines">
          <div className="skel skel-line short" />
          <div className="skel skel-line tall" />
        </div>
      </div>
      <div className="h2-card is-skel" />
      <div className="h2-card is-skel" />
    </div>
  );
}
